import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from } from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  tap,
  switchMap,
  mergeMap,
} from "rxjs/operators";

import * as CategoriesActionTypes from "../../actions/categories/categories.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";

import { CategoriesService } from "src/app/shared/services/categories/categories.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class CategoriesEffects {
  [x: string]: any;

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private categoryService: CategoriesService,
    private tableConfigService: TableConfigService,
    private translate: TranslateService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }

  getCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.getCategories),
      switchMap((action) =>
        this.categoryService
          .getCategoriesElements(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              CategoriesActionTypes.loadCategories({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CategoriesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Categories.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getExcelCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.getExcelCategories),
      switchMap((action) =>
        this.categoryService
          .getCategoriesElements(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              CategoriesActionTypes.loadExcelCategories({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CategoriesActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Categories.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getCategoryUsages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.getCategoryUsages),
      exhaustMap((action) =>
        this.categoryService.getCategoryUsages(action.pageReq).pipe(
          map((result) =>
            CategoriesActionTypes.loadCategoryUsages({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CategoriesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Categories.loadUsagesError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Categories Table Config
  getCategoriesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.getCategoriesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              CategoriesActionTypes.loadCategoriesTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                CategoriesActionTypes.loadCategoriesTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Categories Table Config
  saveCategoriesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.saveCategoriesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            CategoriesActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              CategoriesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.getCategory),
      exhaustMap((action) =>
        this.categoryService.getCategory(action.code).pipe(
          map((result) =>
            CategoriesActionTypes.loadCategory({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CategoriesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Categories.loadCategoryError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCategoryElements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.getCategoryElements),
      exhaustMap((action) => {
        return this.categoryService
          .getCategoriesElementsByCode(action.code)
          .pipe(
            map((result) =>
              CategoriesActionTypes.loadCategoryElements({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CategoriesActionTypes.setLoadingEndState(),
                CategoriesActionTypes.loadCategoryElements({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Categories.loadElementsError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          );
      })
    )
  );

  createCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.createCategory),
      exhaustMap((action) =>
        this.categoryService.createCategory(action).pipe(
          mergeMap((result) => [
            CategoriesActionTypes.resetCreation(),
            CategoriesActionTypes.getCategory({
              code: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Categories.categoryCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              CategoriesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Categories.catergoryFailCreate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActionTypes.updateCategory),
      exhaustMap((action) =>
        this.categoryService.updateCategory(action.category).pipe(
          map((result) => {
            return CategoriesActionTypes.getCategory({
              code: "" + result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Categories.categoryUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              CategoriesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Categories.categoryUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
}
